import React from "react";
import { FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "@/components/common/loading-spinner";
import { useCheckoutAgreementsData } from "../hooks/use-checkout-agreements";

export const CheckoutAgreementsSection: React.FC = () => {
  const { t } = useTranslation();
  const { agreementTypeGroups, isLoading, error, hasSelectedAddress } =
    useCheckoutAgreementsData();

  const decodeHtmlEntities = (value: string) => {
    if (typeof window === "undefined") return value;

    const textarea = document.createElement("textarea");
    textarea.innerHTML = value;

    // Some payloads can be encoded more than once.
    let decoded = textarea.value;
    for (let i = 0; i < 2; i += 1) {
      textarea.innerHTML = decoded;
      const next = textarea.value;
      if (next === decoded) break;
      decoded = next;
    }

    return decoded;
  };

  const getMergedAgreementHtml = (group: (typeof agreementTypeGroups)[number]) =>
    group.agreements
      .map((agreement) => decodeHtmlEntities((agreement.html || "").trim()))
      .filter(Boolean)
      .join("<br /><br />");

  return (
    <div className="border border-[#e3e8ef] dark:border-gray-700 rounded-[12px] overflow-hidden">
      <div className="bg-[#fcfcfd] dark:bg-gray-800 border-b border-[#e3e8ef] dark:border-gray-700 px-3 md:px-4 py-2.5 md:py-3">
        <h2 className="text-base md:text-lg lg:text-[20px] font-semibold leading-6 md:leading-7 lg:leading-[30px] text-[#121926] dark:text-gray-100 flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#6172f3]" />
          {t("checkout.agreementsTitle")}
        </h2>
      </div>

      <div className="p-3 md:p-4 space-y-4">
        {!hasSelectedAddress && (
          <p className="text-sm text-[#364152] dark:text-gray-300">
            {t("checkout.agreementsSelectAddress")}
          </p>
        )}

        {hasSelectedAddress && isLoading && (
          <div className="flex items-center gap-2 text-sm text-[#364152] dark:text-gray-300">
            <LoadingSpinner size="sm" />
            <span>{t("checkout.agreementsLoading")}</span>
          </div>
        )}

        {hasSelectedAddress && !isLoading && error && (
          <p className="text-sm text-red-600 dark:text-red-400">
            {t("checkout.agreementsError")}
          </p>
        )}

        {hasSelectedAddress &&
          !isLoading &&
          !error &&
          agreementTypeGroups.length === 0 && (
            <p className="text-sm text-[#364152] dark:text-gray-300">
              {t("checkout.agreementsEmpty")}
            </p>
          )}

        {hasSelectedAddress &&
          !isLoading &&
          !error &&
          agreementTypeGroups.map((group) => (
            <div
              key={group.agreementName}
              className="min-w-0 rounded-[10px] border border-[#e9eaeb] dark:border-gray-700 bg-white dark:bg-gray-900/30 p-3 md:p-4 space-y-3"
            >
              <h3 className="text-sm md:text-base font-semibold text-[#121926] dark:text-gray-100 break-words">
                {group.agreementTitle}
              </h3>

              <div className="min-w-0 rounded-[8px] border border-[#f1f2f4] dark:border-gray-700 bg-[#fcfcfd] dark:bg-gray-800/60 max-h-[220px] md:max-h-[260px] overflow-x-auto overflow-y-auto px-3 md:px-4 py-3 md:py-4">
                <div
                  className="min-w-0 break-words text-xs md:text-sm leading-relaxed text-[#535862] dark:text-gray-200 [&_*]:max-w-full [&_*]:text-inherit [&_h1]:text-sm [&_h1]:md:text-base [&_h1]:font-semibold [&_h2]:text-sm [&_h2]:md:text-base [&_h2]:font-semibold [&_h3]:text-sm [&_h3]:font-semibold [&_p]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_strong]:font-semibold [&_a]:break-all [&_img]:h-auto [&_img]:max-w-full [&_table]:block [&_table]:w-full [&_table]:max-w-full [&_table]:overflow-x-auto"
                  dangerouslySetInnerHTML={{
                    __html: getMergedAgreementHtml(group),
                  }}
                />
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};
